import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import "../../css/Analytics.css";
import { fetchAnalyticsDashboard, exportAnalyticsDashboard } from "../../service/api/adminApi";
import AreaOperationsMap from "../../components/AreaOperationsMap";
import {
    ResponsiveContainer,
    LineChart,
    Line,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
} from "recharts";

const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
};

const today = formatLocalDate(new Date());
const monthStart = formatLocalDate(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1)
);

const validateDateRange = (nextFilters) => {
    const from = nextFilters?.from_date;
    const to = nextFilters?.to_date;

    if (!from || !to) {
        return "Please select both From Date and To Date.";
    }

    if (new Date(from) > new Date(to)) {
        return "From Date cannot be later than To Date.";
    }

    return "";
};

const defaultFilters = {
    from_date: monthStart,
    to_date: today,
    group_by: "day",
    zone_id: "",
    restaurant_id: "",
    area: "",
    payment_mode: "",
    order_status: "",
    customer_type: "all",
};

const tabs = [
    "overview",
    "orders",
    "revenue",
    "aov",
    "customers",
    "restaurants",
    "areas",
];

const money = (v) => `₹${Number(v || 0).toFixed(2)}`;
const num = (v) => Number(v || 0).toLocaleString("en-IN");

export default function Analytics() {
    const [activeView, setActiveView] = useState("overview");
    const [draftFilters, setDraftFilters] = useState(defaultFilters);
    const [appliedFilters, setAppliedFilters] = useState(defaultFilters);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [filtersOpen, setFiltersOpen] = useState(false);
    const cacheRef = useRef(new Map());
    const requestRef = useRef(null);

    const params = useMemo(() => {
        const clean = {};
        Object.entries(appliedFilters).forEach(([k, v]) => {
            if (v !== "" && v !== null && v !== undefined) clean[k] = v;
        });
        clean.section = activeView;
        return clean;
    }, [activeView, appliedFilters]);

    useEffect(() => {
        requestRef.current?.abort();
        const controller = new AbortController();
        requestRef.current = controller;
        let active = true;

        const load = async () => {
            const validationError = validateDateRange(params);

            if (validationError) {
                if (active) {
                    setErrorMessage(validationError);
                    setLoading(false);
                }
                return;
            }

            const cacheKey = JSON.stringify(params);
            const cached = cacheRef.current.get(cacheKey);
            if (cached) {
                setData((previous) => ({ ...(previous || {}), ...cached }));
                setErrorMessage("");
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setErrorMessage("");
                const res = await fetchAnalyticsDashboard(params, controller.signal);
                if (!active) return;
                cacheRef.current.set(cacheKey, res);
                setData((previous) => ({ ...(previous || {}), ...res }));
            } catch (error) {
                if (error?.name === "CanceledError" || error?.code === "ERR_CANCELED") return;
                if (!active) return;

                if (error?.response?.status === 422) {
                    setErrorMessage(
                        error?.response?.data?.message || "Please check the selected filters."
                    );
                    return;
                }

                const backendMessage =
                    error?.response?.data?.message ||
                    error?.message ||
                    "Failed to load analytics.";

                setErrorMessage(backendMessage);
                console.error(error);
            } finally {
                if (active && requestRef.current === controller) {
                    setLoading(false);
                }
            }
        };

        load();
        return () => {
            active = false;
            controller.abort();
        };
    }, [params]);

    const summary = data?.summary_cards || {};
    const customer = data?.customer_breakdown || {};
    const realtime = data?.realtime || {};
    const funnel = data?.visit_funnel || {};
    const overviewTimeseries = data?.overview_timeseries || [];
    const ordersTimeseries = data?.orders_timeseries || [];
    const revenueTimeseries = data?.revenue_timeseries || [];
    const aovTimeseries = data?.aov_timeseries || [];
    const completedSeries = data?.completed_orders_timeseries || [];
    const cancelledSeries = data?.cancelled_orders_timeseries || [];
    const liveSeries = data?.live_orders_timeseries || [];
    const averageRevenueSeries = data?.average_revenue_timeseries || [];
    const hourlyDistribution = data?.hourly_distribution || data?.peak_hours || [];
    const weekdayBreakdown = data?.weekday_breakdown || [];
    const weekendBreakdown = data?.weekend_breakdown || [];
    const restaurantBreakdown = data?.restaurant_breakdown || [];
    const areaBreakdown = data?.area_breakdown || [];
    const paymentBreakdown = data?.payment_breakdown || [];

    const normalizedAovTimeseries = aovTimeseries.map((r) => ({
        ...r,
        aov: Number(r.aov ?? r.value ?? 0),
    }));

    const overviewTrend = overviewTimeseries;
    const effectiveGrouping = data?.effective_group_by || data?.effective_grouping || data?.group_by;

    const customerSegments = [
        { name: "New Visitors", value: customer.new_visitors || 0 },
        { name: "Returning", value: customer.returning_visitors || 0 },
        { name: "Registered", value: customer.registered_customers || 0 },
        { name: "With Orders", value: customer.customers_with_orders || 0 },
        { name: "Never Ordered", value: customer.registered_but_never_ordered || 0 },
        { name: "Repeat", value: customer.repeat_customers || 0 },
        { name: "Dormant 30d", value: customer.dormant_customers_30_days || 0 },
    ];

    const visitorFunnel = [
        { name: "App Open", value: customer.app_opened_today || 0 },
        { name: "Restaurant", value: customer.restaurant_viewed || 0 },
        { name: "Item", value: customer.item_viewed || 0 },
        { name: "Cart", value: customer.added_to_cart || 0 },
        { name: "Checkout", value: customer.checkout_started || 0 },
        { name: "Orders", value: customer.successful_orders || 0 },
    ];

    const setFilter = useCallback((key, value) => {
        setDraftFilters((previous) => ({ ...previous, [key]: value }));
    }, []);

    const applyFilters = useCallback(() => {
        const validationError = validateDateRange(draftFilters);
        if (validationError) {
            setErrorMessage(validationError);
            return;
        }
        setErrorMessage("");
        setAppliedFilters({ ...draftFilters });
    }, [draftFilters]);

    const quickRange = (type) => {
        const d = new Date();
        let from = new Date();
        let to = new Date();

        if (type === "today") {
            from = d;
            to = d;
        } else if (type === "yesterday") {
            from.setDate(d.getDate() - 1);
            to.setDate(d.getDate() - 1);
        } else if (type === "7") {
            from.setDate(d.getDate() - 6);
        } else if (type === "30") {
            from.setDate(d.getDate() - 29);
        } else {
            from = new Date(d.getFullYear(), d.getMonth(), 1);
        }

        const nextRange = {
            from_date: formatLocalDate(from),
            to_date: formatLocalDate(to),
        };
        setDraftFilters((p) => ({
            ...p,
            ...nextRange,
        }));
        setAppliedFilters((p) => ({ ...p, ...nextRange }));
    };

    const resetFilters = () => {
        setDraftFilters({ ...defaultFilters });
        setAppliedFilters({ ...defaultFilters });
        setErrorMessage("");
    };

    const handleExport = async () => {
        const validationError = validateDateRange(appliedFilters);

        if (validationError) {
            setErrorMessage(validationError);
            return;
        }

        try {
            setErrorMessage("");
            const exportParams = Object.fromEntries(
                Object.entries(appliedFilters).filter(([, value]) => value !== "" && value != null)
            );
            const res = await exportAnalyticsDashboard(exportParams);
            const blob = new Blob([JSON.stringify(res, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `analytics-${appliedFilters.from_date}-to-${appliedFilters.to_date}.json`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (error) {
            setErrorMessage(
                error?.response?.data?.message || "Analytics export failed."
            );
        }
    };

    return (
        <div className="analytics-page">
            <div className="analytics-head">
                <div>
                    <h2>Analytics Dashboard</h2>
                    <p>Enterprise operations intelligence for orders, customers, stores, areas and realtime.</p>
                </div>
                <button className="analytics-export" onClick={handleExport}>Export</button>
            </div>

            <button type="button" className="analytics-filter-toggle" onClick={() => setFiltersOpen((open) => !open)} aria-expanded={filtersOpen}>
                {filtersOpen ? "Hide Filters" : "Show Filters"}
            </button>
            <div className={"analytics-filter-card " + (filtersOpen ? "open" : "")}>
                <input
                    type="date"
                    value={draftFilters.from_date}
                    onChange={(e) => {
                        const nextFrom = e.target.value;

                        setDraftFilters((previous) => {
                            const next = { ...previous, from_date: nextFrom };

                            if (
                                nextFrom &&
                                previous.to_date &&
                                new Date(nextFrom) > new Date(previous.to_date)
                            ) {
                                next.to_date = nextFrom;
                            }

                            return next;
                        });
                    }}
                />
                <input
                    type="date"
                    value={draftFilters.to_date}
                    min={draftFilters.from_date || undefined}
                    onChange={(e) => {
                        const nextTo = e.target.value;

                        setDraftFilters((previous) => {
                            const next = { ...previous, to_date: nextTo };

                            if (
                                nextTo &&
                                previous.from_date &&
                                new Date(nextTo) < new Date(previous.from_date)
                            ) {
                                next.from_date = nextTo;
                            }

                            return next;
                        });
                    }}
                />

                <select value={draftFilters.group_by} onChange={(e) => setFilter("group_by", e.target.value)}>
                    <option value="hour">Hour</option>
                    <option value="day">Day</option>
                    <option value="week">Week</option>
                    <option value="month">Month</option>
                    <option value="year">Year</option>
                </select>

                <input placeholder="Restaurant ID" value={draftFilters.restaurant_id} onChange={(e) => setFilter("restaurant_id", e.target.value)} />
                <input
                    placeholder="Search Area / Pincode / Landmark"
                    value={draftFilters.area}
                    onChange={(e) => setFilter("area", e.target.value)}
                />

                <select value={draftFilters.payment_mode} onChange={(e) => setFilter("payment_mode", e.target.value)}>
                    <option value="">All Payments</option>
                    <option value="COD">COD</option>
                    <option value="ONLINE">Online</option>
                </select>

                <select value={draftFilters.order_status} onChange={(e) => setFilter("order_status", e.target.value)}>
                    <option value="">All Status</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="payment_failed">Payment Failed</option>
                    <option value="live">Live</option>
                </select>

                <select value={draftFilters.customer_type} onChange={(e) => setFilter("customer_type", e.target.value)}>
                    <option value="all">All Customers</option>
                    <option value="new">New</option>
                    <option value="repeat">Repeat</option>
                    <option value="dormant">Dormant</option>
                    <option value="registered_no_order">Registered No Order</option>
                </select>

                <button onClick={() => quickRange("today")}>Today</button>
                <button onClick={() => quickRange("yesterday")}>Yesterday</button>
                <button onClick={() => quickRange("7")}>Last 7 Days</button>
                <button onClick={() => quickRange("30")}>Last 30 Days</button>
                <button onClick={() => quickRange("month")}>This Month</button>
                <button className="analytics-apply" onClick={applyFilters}>Apply Filters</button>
                <button onClick={resetFilters}>Reset Filters</button>
            </div>

            {errorMessage && (
                <div className="analytics-error-banner" role="alert">
                    <div>
                        <strong>Unable to load analytics</strong>
                        <span>{errorMessage}</span>
                    </div>

                    <button
                        type="button"
                        onClick={() => setErrorMessage("")}
                        aria-label="Dismiss error"
                    >
                        {"\\u00d7"}
                    </button>
                </div>
            )}

            <div className="analytics-tabs-scroll">
                {tabs.map((tab) => (
                    <button
                        key={tab}
                        className={`analytics-metric-card ${activeView === tab ? "active" : ""}`}
                        onClick={() => setActiveView(tab)}
                    >
                        <span>{tab}</span>
                        <b>
                            {tab === "overview" && num(summary.total_orders)}
                            {tab === "orders" && num(summary.total_orders)}
                            {tab === "revenue" && money(summary.total_sales)}
                            {tab === "aov" && money(summary.aov || summary.average_order_value)}
                            {tab === "customers" && num(customer.registered_customers)}
                            {tab === "restaurants" && (summary.top_restaurant?.restaurant_name || "--")}
                            {tab === "areas" && num(summary.areas_needing_focus)}
                            {tab === "funnel" && (funnel.available ? `${funnel.checkout_to_order_rate || 0}%` : "--")}
                            {tab === "realtime" && num(realtime.live_orders)}
                        </b>
                        <small>
                            {tab === "areas" && "Need focus"}
                            {tab === "revenue" && "completed sales"}
                            {tab !== "areas" && tab !== "revenue" && "click to view"}
                        </small>
                    </button>
                ))}
            </div>

            {effectiveGrouping && effectiveGrouping !== appliedFilters.group_by && (
                <div className="analytics-grouping-badge">
                    Viewing {String(effectiveGrouping).replace(/^./, (letter) => letter.toUpperCase())}ly Trend
                </div>
            )}
            {loading && <div className="analytics-loading-overlay" role="status">Loading...</div>}

            {activeView === "overview" && (
                <section className="analytics-section">
                    <div className="analytics-grid analytics-grid-4">
                        <MiniCard title="Total Orders" value={num(summary.total_orders)} />
                        <MiniCard title="Completed" value={num(summary.completed_orders)} />
                        <MiniCard title="Cancelled" value={num(summary.cancelled_orders)} />
                        <MiniCard title="Total Sales" value={money(summary.total_sales)} />
                        <MiniCard title="AOV" value={money(summary.aov || summary.average_order_value)} />
                        <MiniCard title="Completion Rate" value={`${summary.completion_rate || 0}%`} />
                        <MiniCard title="Cancellation Rate" value={`${summary.cancellation_rate || 0}%`} />
                        <MiniCard title="Wallet Used" value={money(summary.wallet_used)} />
                        <MiniCard title="Areas Needing Focus" value={num(summary.areas_needing_focus)} />
                        <MiniCard title="Highest Focus Area" value={summary.highest_focus_area?.area || "--"} />
                    </div>

                    <ChartCard title="Orders + Revenue + AOV Trend">
                        <LineChart data={overviewTrend}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="bucket" />
                            <YAxis />
                            <Tooltip />
                            <Line type="monotone" dataKey="orders" strokeWidth={3} />
                            <Line type="monotone" dataKey="revenue" strokeWidth={3} />
                            <Line type="monotone" dataKey="aov" strokeWidth={3} />
                        </LineChart>
                    </ChartCard>

                    <ChartCard title="Payment Breakdown">
                        <BarChart data={paymentBreakdown}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="payment_mode" />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="orders" radius={[8, 8, 0, 0]} />
                        </BarChart>
                    </ChartCard>
                </section>
            )}

            {activeView === "orders" && (
                <section className="analytics-section">
                    <div className="analytics-grid analytics-grid-5">
                        <MiniCard title="Avg Orders / Hour" value={summary.average_orders_per_hour} />
                        <MiniCard title="Avg Orders / Day" value={summary.average_orders_per_day} />
                        <MiniCard title="Avg Orders / Week" value={summary.average_orders_per_week} />
                        <MiniCard title="Avg Orders / Month" value={summary.average_orders_per_month} />
                        <MiniCard title="Avg Orders / Year" value={summary.average_orders_per_year} />
                    </div>

                    <ChartCard title="Orders Trend">
                        <LineChart data={ordersTimeseries}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="bucket" />
                            <YAxis />
                            <Tooltip />
                            <Line type="monotone" dataKey="orders" strokeWidth={3} />
                        </LineChart>
                    </ChartCard>

                    <ChartCard title="Completed / Cancelled / Live Trends">
                        <LineChart data={mergeSeries([
                            ["completed", completedSeries],
                            ["cancelled", cancelledSeries],
                            ["live", liveSeries],
                        ])}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="bucket" />
                            <YAxis />
                            <Tooltip />
                            <Line type="monotone" dataKey="completed" strokeWidth={3} />
                            <Line type="monotone" dataKey="cancelled" strokeWidth={3} />
                            <Line type="monotone" dataKey="live" strokeWidth={3} />
                        </LineChart>
                    </ChartCard>

                    <ChartCard title="Hourly Distribution">
                        <BarChart data={hourlyDistribution}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="hour" />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="orders" radius={[8, 8, 0, 0]} />
                        </BarChart>
                    </ChartCard>

                    <ChartCard title="Weekday Breakdown">
                        <BarChart data={weekdayBreakdown}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="day_name" />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="orders" radius={[8, 8, 0, 0]} />
                        </BarChart>
                    </ChartCard>

                    <ChartCard title="Weekend Breakdown">
                        <BarChart data={weekendBreakdown}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="day_type" />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="orders" radius={[8, 8, 0, 0]} />
                        </BarChart>
                    </ChartCard>
                </section>
            )}

            {activeView === "revenue" && (
                <section className="analytics-section">
                    <div className="analytics-grid analytics-grid-4">
                        <MiniCard title="Total Sales" value={money(summary.total_sales)} />
                        <MiniCard title="AOV" value={money(summary.aov || summary.average_order_value)} />
                        <MiniCard title="Avg Revenue / Day" value={money(summary.average_revenue_per_day)} />
                        <MiniCard title="Avg Revenue / Month" value={money(summary.average_revenue_per_month)} />
                        <MiniCard title="Avg Revenue / Hour" value={money(summary.average_revenue_per_hour)} />
                        <MiniCard title="Avg Revenue / Week" value={money(summary.average_revenue_per_week)} />
                        <MiniCard title="Avg Revenue / Year" value={money(summary.average_revenue_per_year)} />
                        <MiniCard title="Wallet Used" value={money(summary.wallet_used)} />
                    </div>

                    <ChartCard title="Revenue Trend">
                        <LineChart data={revenueTimeseries}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="bucket" />
                            <YAxis />
                            <Tooltip />
                            <Line type="monotone" dataKey="revenue" strokeWidth={3} />
                        </LineChart>
                    </ChartCard>

                    <ChartCard title="Average Revenue Trend">
                        <LineChart data={averageRevenueSeries}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="bucket" />
                            <YAxis />
                            <Tooltip />
                            <Line type="monotone" dataKey="average_revenue" strokeWidth={3} />
                        </LineChart>
                    </ChartCard>
                </section>
            )}

            {activeView === "aov" && (
                <section className="analytics-section">
                    <div className="analytics-grid analytics-grid-4">
                        <MiniCard title="AOV" value={money(summary.aov || summary.average_order_value)} />
                        <MiniCard title="Avg Revenue / Day" value={money(summary.average_revenue_per_day)} />
                        <MiniCard title="Completed Orders" value={num(summary.completed_orders)} />
                        <MiniCard title="Total Sales" value={money(summary.total_sales)} />
                    </div>

                    <ChartCard title="AOV Trend">
                        <LineChart data={normalizedAovTimeseries}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="bucket" />
                            <YAxis />
                            <Tooltip />
                            <Line type="monotone" dataKey="aov" strokeWidth={3} />
                        </LineChart>
                    </ChartCard>
                </section>
            )}
            {activeView === "customers" && (
                <section className="analytics-section">
                    <div className="analytics-grid analytics-grid-5">
                        <MiniCard title="New Visitors" value={customer.new_visitors} />
                        <MiniCard title="Returning Visitors" value={customer.returning_visitors} />
                        <MiniCard title="Registered" value={customer.registered_customers} />
                        <MiniCard title="With Orders" value={customer.customers_with_orders} />
                        <MiniCard title="Never Ordered" value={customer.registered_but_never_ordered} />
                        <MiniCard title="One Time" value={customer.one_time_customers} />
                        <MiniCard title="Repeat" value={customer.repeat_customers} />
                        <MiniCard title="Dormant 30d" value={customer.dormant_customers_30_days} />
                        <MiniCard title="Dormant 60d" value={customer.dormant_customers_60_days} />
                        <MiniCard title="Dormant 90d" value={customer.dormant_customers_90_days} />
                        <MiniCard title="Avg Orders / Customer" value={customer.average_orders_per_customer} />
                        <MiniCard title="Avg Spend / Customer" value={money(customer.average_spend_per_customer)} />
                        <MiniCard title="App Opened Today" value={customer.app_opened_today} />
                        <MiniCard title="Restaurant Viewed" value={customer.restaurant_viewed} />
                        <MiniCard title="Item Viewed" value={customer.item_viewed} />
                        <MiniCard title="Added To Cart" value={customer.added_to_cart} />
                        <MiniCard title="Checkout Started" value={customer.checkout_started} />
                        <MiniCard title="Abandoned Cart" value={customer.abandoned_cart} />
                        <MiniCard title="Abandoned Checkout" value={customer.abandoned_checkout} />
                        <MiniCard title="Successful Orders" value={customer.successful_orders} />
                    </div>

                    <ChartCard title="Customer Segments">
                        <BarChart data={customerSegments}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="value" radius={[8, 8, 0, 0]} />
                        </BarChart>
                    </ChartCard>

                    <ChartCard title="Visitor Funnel">
                        <BarChart data={visitorFunnel}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="value" radius={[8, 8, 0, 0]} />
                        </BarChart>
                    </ChartCard>
                </section>
            )}

            {activeView === "restaurants" && (
                <section className="analytics-section">
                    <ChartCard title="Top Restaurants by Revenue">
                        <BarChart data={restaurantBreakdown.slice(0, 15)}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="restaurant_name" />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="revenue" radius={[8, 8, 0, 0]} />
                        </BarChart>
                    </ChartCard>

                    <AnalyticsTable
                        columns={["Restaurant", "Orders", "Completed", "Cancelled", "Revenue", "AOV", "Cancel Rate", "Avg Time"]}
                        rows={restaurantBreakdown.map((r) => [
                            r.restaurant_name,
                            r.total_orders,
                            r.completed_orders,
                            r.cancelled_orders,
                            money(r.revenue),
                            money(r.average_order_value),
                            `${r.cancellation_rate || 0}%`,
                            `${r.avg_completion_minutes || 0} min`,
                        ])}
                    />
                </section>
            )}

            {activeView === "areas" && (
                <section className="analytics-section analytics-areas-section">
                    <AreaOperationsMap areaBreakdown={areaBreakdown} />
                </section>
            )}
            {activeView === "funnel" && (
                <section className="analytics-section">
                    {!funnel.available ? (
                        <div className="analytics-tracking-card">
                            <strong>Tracking setup required</strong>
                            <span>{funnel.message || "Session and event tracking is not available yet."}</span>
                        </div>
                    ) : (
                        <>
                            <div className="analytics-funnel">
                                {[
                                    ["App Open", funnel.app_open_sessions],
                                    ["Restaurant View", funnel.restaurant_view_sessions],
                                    ["Item View", funnel.item_view_sessions],
                                    ["Add To Cart", funnel.add_to_cart_sessions],
                                    ["Checkout", funnel.checkout_started_sessions],
                                    ["Payment", funnel.payment_started_sessions],
                                    ["Order Placed", funnel.order_placed_sessions],
                                    ["Order Completed", funnel.order_completed_sessions],
                                ].map(([label, value]) => (
                                    <div className="analytics-funnel-step" key={label}>
                                        <span>{label}</span>
                                        <b>{value || 0}</b>
                                    </div>
                                ))}
                            </div>

                            <div className="analytics-grid analytics-grid-5">
                                <MiniCard title="Abandoned After Visit" value={funnel.abandoned_after_visit} />
                                <MiniCard title="Abandoned After Cart" value={funnel.abandoned_after_cart} />
                                <MiniCard title="Abandoned After Checkout" value={funnel.abandoned_after_checkout} />
                                <MiniCard title="Checkout â†’ Order" value={`${funnel.checkout_to_order_rate || 0}%`} />
                                <MiniCard title="Restaurant â†’ Order" value={`${funnel.restaurant_view_to_order_rate || 0}%`} />
                            </div>
                        </>
                    )}
                </section>
            )}

            {activeView === "realtime" && (
                <section className="analytics-section">
                    <div className="analytics-grid analytics-grid-4">
                        <MiniCard title="Today Orders" value={realtime.today_orders} />
                        <MiniCard title="Today Sales" value={money(realtime.today_sales)} />
                        <MiniCard title="Live Orders" value={realtime.live_orders} />
                        <MiniCard title="Cancelled Today" value={realtime.cancelled_today} />
                        <MiniCard title="Payment Failed Today" value={realtime.payment_failed_today} />
                        <MiniCard title="Active Sessions 5m" value={realtime.active_sessions_last_5_min} />
                        <MiniCard title="Active Sessions 15m" value={realtime.active_sessions_last_15_min} />
                        <MiniCard title="Active Sessions 30m" value={realtime.active_sessions_last_30_min} />
                        <MiniCard title="Checkout Users 15m" value={realtime.checkout_users_last_15_min} />
                        <MiniCard title="Cart Users 15m" value={realtime.cart_users_last_15_min} />
                        <MiniCard title="Abandoned Checkout 60m" value={realtime.abandoned_checkout_last_60_min} />
                    </div>
                </section>
            )}
        </div>
    );
}

function mergeSeries(seriesList) {
    const map = {};
    seriesList.forEach(([key, rows]) => {
        rows.forEach((r) => {
            map[r.bucket] = { ...(map[r.bucket] || { bucket: r.bucket }), [key]: Number(r[key] || r.orders || 0) };
        });
    });
    return Object.values(map);
}

const MiniCard = memo(function MiniCard({ title, value }) {
    return (
        <div className="analytics-mini-card">
            <span>{title}</span>
            <b>{value ?? 0}</b>
        </div>
    );
});

const ChartCard = memo(function ChartCard({ title, children }) {
    return (
        <div className="analytics-chart-card">
            <h3>{title}</h3>
            <div className="analytics-chart-scroll">
                <div className="analytics-chart-inner">
                    <ResponsiveContainer width="100%" height={320}>
                        {children}
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
});

const AnalyticsTable = memo(function AnalyticsTable({ columns, rows }) {
    return (
        <div className="analytics-table-card">
            <div className="analytics-table-wrap">
                <table>
                    <thead>
                    <tr>{columns.map((c) => <th key={c}>{c}</th>)}</tr>
                    </thead>
                    <tbody>
                    {rows.length === 0 ? (
                        <tr><td colSpan={columns.length}>No data found</td></tr>
                    ) : rows.map((row, i) => (
                        <tr key={i}>{row.map((cell, j) => <td key={j}>{cell}</td>)}</tr>
                    ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
});